import type { SyncResult } from './types.js';

/**
 * Live sync status observable. Mirrors SyncState.kt — used by both the
 * launcher's status pill and any module that wants to react.
 */
export type SyncStatus = 'IDLE' | 'SYNCING' | 'OK' | 'ERROR' | 'NOT_CONFIGURED';

interface State {
  status: SyncStatus;
  lastSyncMs: number;
  lastError: string;
  lastResult: SyncResult | null;
}

type Listener = (s: Readonly<State>) => void;

class SyncStateStore {
  private state: State = { status: 'NOT_CONFIGURED', lastSyncMs: 0, lastError: '', lastResult: null };
  private listeners = new Set<Listener>();

  get snapshot(): Readonly<State> { return { ...this.state }; }

  setNotConfigured(): void { this.patch({ status: 'NOT_CONFIGURED', lastError: '' }); }
  setIdle(): void         { this.patch({ status: 'IDLE', lastError: '' }); }
  markStarted(): void     { this.patch({ status: 'SYNCING', lastError: '' }); }

  markFinished(result: SyncResult): void {
    this.patch({
      status: result.success ? 'OK' : 'ERROR',
      lastSyncMs: result.completedAtMs,
      lastError: result.success ? '' : (result.errors[0] ?? 'Unknown error'),
      lastResult: result,
    });
  }

  markError(msg: string): void { this.patch({ status: 'ERROR', lastError: msg }); }

  /** Throttle helper: did we sync within the last `withinMs` milliseconds? */
  isRecentlySynced(withinMs: number): boolean {
    return this.state.status === 'OK' && Date.now() - this.state.lastSyncMs < withinMs;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.snapshot);
    return () => this.listeners.delete(fn);
  }

  private patch(next: Partial<State>): void {
    this.state = { ...this.state, ...next };
    for (const l of this.listeners) l(this.snapshot);
  }
}

export const syncState = new SyncStateStore();
